import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icone } from './Icone'

/**
 * Pile des superpositions ouvertes : Échap ne ferme que la plus récente
 * (la modale d'abonnement ouverte PAR-DESSUS la fiche ne doit pas fermer les deux).
 */
const pile: symbol[] = []

const FOCALISABLES =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

/**
 * Superposition accessible : portail vers `document.body` (une carte animée
 * deviendrait sinon le bloc conteneur du `position:fixed`), rôle `dialog`,
 * focus enfermé, Échap, restitution du focus à la fermeture.
 * Rendue seulement quand elle est ouverte — le parent la monte et la démonte.
 */
export function Modale({
  titre,
  sousTitre,
  surFermer,
  variante = 'modale',
  pied,
  children,
}: {
  titre: string
  sousTitre?: string
  surFermer: () => void
  variante?: 'modale' | 'tiroir'
  pied?: ReactNode
  children: ReactNode
}) {
  const idTitre = useId()
  const boite = useRef<HTMLDivElement>(null)
  const fermer = useRef(surFermer)
  fermer.current = surFermer

  useEffect(() => {
    const precedent = document.activeElement as HTMLElement | null
    const moi = Symbol('superposition')
    pile.push(moi)
    const premier = boite.current?.querySelector<HTMLElement>('[data-focus-initial]') ??
      boite.current?.querySelector<HTMLElement>(FOCALISABLES)
    premier?.focus()

    const touche = (e: KeyboardEvent) => {
      if (pile[pile.length - 1] !== moi) return
      if (e.key === 'Escape') {
        e.stopPropagation()
        fermer.current()
        return
      }
      if (e.key !== 'Tab' || !boite.current) return
      const liste = Array.from(boite.current.querySelectorAll<HTMLElement>(FOCALISABLES))
      const debut = liste[0]
      const fin = liste[liste.length - 1]
      if (!debut || !fin) return
      if (e.shiftKey && document.activeElement === debut) {
        e.preventDefault()
        fin.focus()
      } else if (!e.shiftKey && document.activeElement === fin) {
        e.preventDefault()
        debut.focus()
      }
    }
    document.addEventListener('keydown', touche)
    return () => {
      document.removeEventListener('keydown', touche)
      const i = pile.indexOf(moi)
      if (i >= 0) pile.splice(i, 1)
      precedent?.focus?.()
    }
  }, [])

  return createPortal(
    <div
      className={`voile ${variante}`}
      onMouseDown={(e) => e.target === e.currentTarget && surFermer()}
    >
      <div
        className="boite"
        ref={boite}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitre}
      >
        <div className="boite-tete">
          <div>
            <h2 id={idTitre}>{titre}</h2>
            {sousTitre && <p className="sous">{sousTitre}</p>}
          </div>
          <button type="button" className="btn-rond" onClick={surFermer} aria-label="Fermer">
            <Icone nom="x" />
          </button>
        </div>
        <div className="boite-corps">{children}</div>
        {pied && <div className="boite-pied">{pied}</div>}
      </div>
    </div>,
    document.body,
  )
}
